/**
 * Namespaced localStorage cache with TTL + content-version invalidation.
 *
 * Each entry is stored under `${namespace}:cache:${key}` and wraps the
 * payload with metadata:
 *   { v: contentVersion, t: writeTimestampMs, d: <payload> }
 *
 * A read returns the payload ONLY if:
 *   1. the entry parses cleanly, AND
 *   2. the stored contentVersion matches the current expected one, AND
 *   3. the entry is not older than cacheTtlMs (or ttl is disabled).
 *
 * All other cases resolve to a miss + an invalidation event.
 */
import { emit, CMS_EVENTS } from "./cmsTelemetry";

const fullKey = (config, key) => `${config.cacheNamespace}:cache:${key}`;

const safeStorage = () => {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

export function read(key, config) {
  const ls = safeStorage();
  if (!ls) return null;
  const raw = ls.getItem(fullKey(config, key));
  if (!raw) {
    emit(CMS_EVENTS.CACHE_MISS, { key });
    return null;
  }
  let entry;
  try { entry = JSON.parse(raw); } catch {
    invalidate(key, config, "corrupt");
    return null;
  }
  if (!entry || typeof entry !== "object") {
    invalidate(key, config, "malformed");
    return null;
  }
  // Version check — only when a version was baked in at build time.
  if (config.contentVersion && entry.v && entry.v !== config.contentVersion) {
    emit(CMS_EVENTS.VERSION_MISMATCH, {
      key, cached: entry.v, expected: config.contentVersion,
    });
    invalidate(key, config, "version-mismatch");
    return null;
  }
  // TTL check.
  if (config.cacheTtlMs > 0 && entry.t && Date.now() - entry.t > config.cacheTtlMs) {
    invalidate(key, config, "expired");
    return null;
  }
  emit(CMS_EVENTS.CACHE_HIT, { key });
  return entry.d;
}

export function write(key, value, config) {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.setItem(
      fullKey(config, key),
      JSON.stringify({ v: config.contentVersion || null, t: Date.now(), d: value })
    );
  } catch {
    /* quota / privacy — ignore */
  }
}

export function invalidate(key, config, reason = "manual") {
  const ls = safeStorage();
  if (!ls) return;
  try { ls.removeItem(fullKey(config, key)); } catch { /* ignore */ }
  emit(CMS_EVENTS.CACHE_INVALIDATE, { key, reason });
}
